import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Mail, MapPin, Calendar, Star, DollarSign, Briefcase, MessageCircle, Heart, ShieldCheck, Crown, Globe } from 'lucide-react';
import { formatEthnicity } from '@/utils/ethnicity';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Card = ({ children, className = '', ...rest }) => (
  <div className={`bg-[#2D2D52]/40 backdrop-blur-xl border border-white/5 shadow-[0_8px_32px_0_rgba(0,0,0,0.4)] rounded-2xl ${className}`} {...rest}>{children}</div>
);

const Stat = ({ icon: Icon, label, value, color = 'text-[#A78BFA]' }) => (
  <Card className="p-4">
    <div className="flex items-center gap-2 mb-1.5">
      <Icon size={14} className={color} />
      <span className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">{label}</span>
    </div>
    <p className="text-xl font-bold text-white">{value}</p>
  </Card>
);

const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return s; }
};

export default function MasterUserDetail() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem('masterToken') || localStorage.getItem('master_token') || localStorage.getItem('token');
      try {
        const res = await fetch(`${API}/master/users/${userId}/full`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`Failed: ${res.status}`);
        const json = await res.json();
        setData(json);
      } catch (e) {
        setError(e.message || 'Failed to load user');
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [userId]);

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="animate-spin text-[#A78BFA]" /></div>;
  if (error) return <div className="text-red-400 text-sm">{error} — <Link to="/master/users" className="text-[#A78BFA] underline">Back to users</Link></div>;
  if (!data) return null;

  const { user, vendor, services, bookings_as_buyer, bookings_as_seller, reviews_received, reviews_given, stats, founding_vendor, founding_nonprofit } = data;
  const role = user?.roles?.[0] || (vendor ? 'vendor' : 'consumer');

  return (
    <div className="space-y-6" data-testid="master-user-detail">
      <Link to="/master/users" className="inline-flex items-center gap-2 text-sm text-[#9CA3AF] hover:text-white">
        <ArrowLeft size={14} /> Back to All Users
      </Link>

      {/* Header */}
      <Card className="p-6">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
            {user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || '?'}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold text-white truncate" data-testid="user-detail-name">{user?.name || 'No name'}</h1>
            <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-[#9CA3AF]">
              <span className="flex items-center gap-1"><Mail size={12} />{user?.email}</span>
              {user?.location && <span className="flex items-center gap-1"><MapPin size={12} />{user.location}</span>}
              <span className="flex items-center gap-1"><Calendar size={12} />Joined {fmtDate(user?.created_at)}</span>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <span className="px-2.5 py-1 rounded-md text-[11px] font-semibold bg-purple-500/15 text-purple-400 border border-purple-500/20">{role}</span>
              {user?.is_active === false && <span className="px-2.5 py-1 rounded-md text-[11px] font-semibold bg-red-500/15 text-red-400 border border-red-500/20">Deactivated</span>}
              {vendor?.id_verified && <span className="px-2.5 py-1 rounded-md text-[11px] font-semibold bg-green-500/15 text-green-400 border border-green-500/20 flex items-center gap-1"><ShieldCheck size={11} />ID Verified</span>}
              {vendor?.stripe_connected && <span className="px-2.5 py-1 rounded-md text-[11px] font-semibold bg-cyan-500/15 text-cyan-400 border border-cyan-500/20">Stripe Connected</span>}
              {founding_vendor && <span className="px-2.5 py-1 rounded-md text-[11px] font-semibold bg-amber-500/15 text-amber-400 border border-amber-500/20 flex items-center gap-1"><Crown size={11} />Founding Vendor #{founding_vendor.seat_number}</span>}
              {founding_nonprofit && <span className="px-2.5 py-1 rounded-md text-[11px] font-semibold bg-amber-500/15 text-amber-400 border border-amber-500/20">Founding Non-Profit</span>}
            </div>
          </div>
          {vendor && (
            <a href={`/vendor/${vendor.vendor_id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#8B5CF6]/20 border border-[#8B5CF6]/40 text-white text-sm hover:bg-[#8B5CF6]/30">
              <Globe size={14} />View public profile
            </a>
          )}
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Briefcase} label="Services" value={stats.services_count} color="text-[#A78BFA]" />
        <Stat icon={DollarSign} label="Bookings (sold)" value={stats.bookings_as_seller_count} color="text-[#86EFAC]" />
        <Stat icon={DollarSign} label="Bookings (bought)" value={stats.bookings_as_buyer_count} color="text-[#FCD34D]" />
        <Stat icon={MessageCircle} label="Messages" value={stats.messages_sent + stats.messages_received} color="text-[#67E8F9]" />
        <Stat icon={MessageCircle} label="Inquiries received" value={stats.inquiries_count} color="text-[#67E8F9]" />
        <Stat icon={Heart} label="Tips given" value={stats.tips_given} color="text-pink-400" />
        <Stat icon={Heart} label="Tips received" value={stats.tips_received} color="text-pink-400" />
        <Stat icon={Star} label="Reviews received" value={reviews_received.length} color="text-amber-400" />
      </div>

      {/* Vendor Profile */}
      {vendor && (
        <Card className="p-6">
          <h2 className="text-lg font-bold text-white mb-4">Vendor Profile</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div><span className="text-[#9CA3AF]">Business name: </span><span className="text-white">{vendor.business_name || '—'}</span></div>
            <div><span className="text-[#9CA3AF]">Industry: </span><span className="text-white">{vendor.category_name || vendor.subcategory_name || '—'}</span></div>
            <div><span className="text-[#9CA3AF]">Location: </span><span className="text-white">{vendor.location || '—'}</span></div>
            <div><span className="text-[#9CA3AF]">Ethnicity: </span><span className="text-white">{formatEthnicity(vendor.ethnicity) || '—'}</span></div>
            <div><span className="text-[#9CA3AF]">Age (private): </span><span className="text-white">{vendor.age || '—'}</span></div>
            <div><span className="text-[#9CA3AF]">Gender: </span><span className="text-white">{vendor.gender || '—'}</span></div>
            <div><span className="text-[#9CA3AF]">Membership: </span><span className="text-white">{vendor.membership_level || 'startup'}</span></div>
            <div><span className="text-[#9CA3AF]">Rating: </span><span className="text-white">{vendor.rating ? `${vendor.rating.toFixed(1)} (${vendor.review_count || 0} reviews)` : 'No reviews yet'}</span></div>
          </div>
          {vendor.description && (
            <div className="mt-4 p-3 rounded-xl bg-white/5">
              <p className="text-[#9CA3AF] text-xs uppercase tracking-wider mb-1">Description</p>
              <p className="text-white text-sm leading-relaxed">{vendor.description}</p>
            </div>
          )}
        </Card>
      )}

      {/* Services */}
      {services?.length > 0 && (
        <Card className="p-6">
          <h2 className="text-lg font-bold text-white mb-4">Services ({services.length})</h2>
          <div className="space-y-2">
            {services.map((s) => (
              <div key={s.service_id} className="p-3 rounded-xl bg-white/5 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium truncate">{s.title}</p>
                  <p className="text-[#9CA3AF] text-xs truncate">{s.description?.substring(0, 100)}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-amber-400 text-sm font-bold">${s.price}</p>
                  <p className="text-[#64748B] text-[10px]">{fmtDate(s.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Bookings */}
      {(bookings_as_seller?.length > 0 || bookings_as_buyer?.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {bookings_as_seller?.length > 0 && (
            <Card className="p-6">
              <h2 className="text-lg font-bold text-white mb-4">Bookings Sold ({bookings_as_seller.length})</h2>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {bookings_as_seller.map((b) => (
                  <div key={b.booking_id} className="p-2.5 rounded-lg bg-white/5 text-xs">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white font-medium truncate">{b.service_title || b.service_id}</span>
                      <span className="text-amber-400 font-bold">${b.amount}</span>
                    </div>
                    <div className="flex items-center justify-between text-[#9CA3AF]">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] ${b.status === 'completed' ? 'bg-green-500/15 text-green-400' : b.status === 'cancelled' ? 'bg-red-500/15 text-red-400' : 'bg-blue-500/15 text-blue-400'}`}>{b.status}</span>
                      <span>{fmtDate(b.created_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
          {bookings_as_buyer?.length > 0 && (
            <Card className="p-6">
              <h2 className="text-lg font-bold text-white mb-4">Bookings Purchased ({bookings_as_buyer.length})</h2>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {bookings_as_buyer.map((b) => (
                  <div key={b.booking_id} className="p-2.5 rounded-lg bg-white/5 text-xs">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white font-medium truncate">{b.service_title || b.service_id}</span>
                      <span className="text-amber-400 font-bold">${b.amount}</span>
                    </div>
                    <div className="flex items-center justify-between text-[#9CA3AF]">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] ${b.status === 'completed' ? 'bg-green-500/15 text-green-400' : b.status === 'cancelled' ? 'bg-red-500/15 text-red-400' : 'bg-blue-500/15 text-blue-400'}`}>{b.status}</span>
                      <span>{fmtDate(b.created_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Reviews */}
      {(reviews_received?.length > 0 || reviews_given?.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {reviews_received?.length > 0 && (
            <Card className="p-6">
              <h2 className="text-lg font-bold text-white mb-4">Reviews Received ({reviews_received.length})</h2>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {reviews_received.map((r, i) => (
                  <div key={i} className="p-2.5 rounded-lg bg-white/5 text-xs">
                    <div className="flex items-center gap-1 mb-1">{[...Array(5)].map((_, j) => <Star key={j} size={10} className={j < (r.rating || 0) ? 'text-amber-400 fill-amber-400' : 'text-[#27272a]'} />)}</div>
                    <p className="text-white">{r.comment || '—'}</p>
                    <p className="text-[#64748B] mt-1">{fmtDate(r.created_at)}</p>
                  </div>
                ))}
              </div>
            </Card>
          )}
          {reviews_given?.length > 0 && (
            <Card className="p-6">
              <h2 className="text-lg font-bold text-white mb-4">Reviews Written ({reviews_given.length})</h2>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {reviews_given.map((r, i) => (
                  <div key={i} className="p-2.5 rounded-lg bg-white/5 text-xs">
                    <div className="flex items-center gap-1 mb-1">{[...Array(5)].map((_, j) => <Star key={j} size={10} className={j < (r.rating || 0) ? 'text-amber-400 fill-amber-400' : 'text-[#27272a]'} />)}</div>
                    <p className="text-white">{r.comment || '—'}</p>
                    <p className="text-[#64748B] mt-1">{fmtDate(r.created_at)}</p>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
